<template>
  <div class="bg-gray">
    <div class="form-content">
      <el-divider>{{ $T('房间分配') }}</el-divider>
      <p class="hint" v-if="totalRooms > 0">
        {{ $T('已分配') }}: {{ assignedRooms }} / {{ totalRooms }}
      </p>

      <el-table :data="assignmentList" border stripe size="mini" style="width:100%" :empty-text="$T('暂无数据')">
        <el-table-column type="index" width="50" :label="$T('序号')"></el-table-column>
        <el-table-column :label="$T('预订酒店')" min-width="140">
          <template slot-scope="scope">
            <span>{{ scope.row.hotel_title || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$T('房型')" min-width="120">
          <template slot-scope="scope">
            <span>{{ scope.row.room_type_name || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$T('入住退房')" min-width="120">
          <template slot-scope="scope">
            <div class="cell-stack readonly-dates">
              <span>{{ scope.row.checkin_date || '-' }}</span>
              <span>{{ scope.row.checkout_date || '-' }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column :label="$T('人员')" min-width="160">
          <template slot-scope="scope">
            <div class="cell-stack member-cell">
              <el-autocomplete
                class="inline-input"
                v-model="scope.row.member1"
                :fetch-suggestions="queryMemberSearch(scope.$index, 'member1')"
                :placeholder="$T('人员1')"
                clearable
                @select="onMemberAutocompleteSelect(scope.row, $event, 'member1')"
                @input="onMember1Change(scope.row)"
              ></el-autocomplete>
              <el-autocomplete
                v-if="getRoomLimit(scope.row) >= 2"
                class="inline-input"
                v-model="scope.row.member2"
                :fetch-suggestions="queryMemberSearch(scope.$index, 'member2')"
                :placeholder="$T('人员2')"
                clearable
                @select="onMemberAutocompleteSelect(scope.row, $event, 'member2')"
              ></el-autocomplete>
            </div>
          </template>
        </el-table-column>
        <el-table-column :label="$T('价格')" min-width="90">
          <template slot-scope="scope">
            <span>{{ Number(scope.row.price || 0).toFixed(2) }} USD</span>
          </template>
        </el-table-column>
      </el-table>

      <div class="summary">
        <span>{{ $T('房间数') }}: {{ totalRooms }}</span>
      </div>

      <div class="footer-button">
        <el-button type="primary" :loading="saveLoading" @click="onSave">{{ $T('保存') }}</el-button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.hint {
  margin: 0 0 12px;
  font-size: 13px;
  color: #909399;
}
.summary {
  margin-top: 12px;
  font-size: 14px;
  color: #606266;
  display: flex;
  justify-content: flex-end;
  gap: 20px;
}
.footer-button {
  margin-top: 16px;
  text-align: center;
}
.cell-stack {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.readonly-dates span {
  font-size: 12px;
  color: #606266;
  line-height: 1.4;
}
.cell-stack /deep/ .inline-input,
.cell-stack /deep/ .el-input {
  width: 100%;
}
.form-content /deep/ .el-table .cell {
  overflow: visible;
}
</style>

<script>
import {
  normalizeMemberOptionList,
  collectExcludedMemberNames,
  filterMemberSelectOptions,
} from '@/utils/teamMemberSelect';

function normalizeRoomLimit(limit) {
  const n = Number(limit);
  if (!n || Number.isNaN(n) || n < 1) {
    return 1;
  }
  return n >= 2 ? 2 : 1;
}

function memberArrayToFields(member, limit) {
  const list = Array.isArray(member) ? member : [];
  const lim = normalizeRoomLimit(limit);
  return {
    member1: list[0] != null ? String(list[0]) : '',
    member2: lim >= 2 && list[1] != null ? String(list[1]) : '',
  };
}

function normalizeAssignmentRowFromApi(row) {
  const r = Object.assign({}, row);
  r.limit = normalizeRoomLimit(r.limit);
  const memberFields = memberArrayToFields(r.member, r.limit);
  r.member1 = memberFields.member1;
  r.member2 = memberFields.member2;
  delete r.member;
  r.price = r.price != null ? Number(r.price) : 0;
  r.checkin_date = r.checkin_date || '';
  r.checkout_date = r.checkout_date || '';
  return r;
}

export default {
  name: 'SaiApplyFormStep3RoomMember',
  data() {
    return {
      saveLoading: false,
      formData: {
        id: 0,
      },
      teamMemberOptions: [],
      assignmentList: [],
      totalRooms: 0,
      assignedRooms: 0,
    };
  },
  mounted() {
    this.formData.id = this.$route.query.id;
    document.title = this.$T('房间分配');
    if (this.formData.id) {
      this.fetch();
    }
  },
  methods: {
    fetch() {
      const id = this.formData.id;
      this.$api.post('/api/isai/roomMemberDetail', { id }, (res) => {
        const data = res.data || {};
        this.teamMemberOptions = normalizeMemberOptionList(data.member_options || []);
        this.totalRooms = Number(data.total_rooms || 0);
        this.assignedRooms = Number(data.assigned_rooms || 0);
        const list = data.assignment_list || [];
        this.assignmentList = list.map((row) => normalizeAssignmentRowFromApi(row));
        if (this.teamMemberOptions.length === 0) {
          this.loadTeamMemberOptionsFromStep2();
        }
      }, (res) => {
        this.$message.error(res.msg || this.$T('加载失败'));
      });
    },
    loadTeamMemberOptionsFromStep2() {
      this.$api.post('/api/isai/step2detail', { id: this.formData.id }, (res) => {
        const list = (res.data && res.data.member_table_list) || [];
        this.teamMemberOptions = normalizeMemberOptionList(list);
      }, () => {});
    },
    getMemberSelectOptions(rowIndex, slot) {
      const excluded = collectExcludedMemberNames(this.assignmentList, rowIndex, slot);
      return filterMemberSelectOptions(this.teamMemberOptions, excluded);
    },
    queryMemberSearch(rowIndex, slot) {
      return (queryString, cb) => {
        const q = (queryString || '').trim().toLowerCase();
        let options = this.getMemberSelectOptions(rowIndex, slot);
        if (q) {
          options = options.filter((opt) => opt.value.toLowerCase().indexOf(q) !== -1);
        }
        cb(options.map((opt) => ({ value: opt.value })));
      };
    },
    onMemberAutocompleteSelect(row, item, slot) {
      if (!item || item.value == null) {
        return;
      }
      if (slot === 'member1') {
        row.member1 = String(item.value);
        this.onMember1Change(row);
      } else if (slot === 'member2') {
        row.member2 = String(item.value);
      }
    },
    onMember1Change(row) {
      const n1 = (row.member1 || '').trim();
      const n2 = (row.member2 || '').trim();
      if (n1 && n2 && n1 === n2) {
        row.member2 = '';
      }
    },
    getRoomLimit(row) {
      return normalizeRoomLimit(row.limit);
    },
    buildMemberArray(row) {
      const limit = this.getRoomLimit(row);
      const member = [];
      const name1 = (row.member1 || '').trim();
      const name2 = (row.member2 || '').trim();
      if (name1) {
        member.push(name1);
      }
      if (limit >= 2 && name2) {
        member.push(name2);
      }
      return member;
    },
    onSave() {
      if (!this.assignmentList.length) {
        this.$message.warning(this.$T('请先完成住宿预订'));
        return;
      }
      for (let i = 0; i < this.assignmentList.length; i++) {
        const row = this.assignmentList[i];
        const n = i + 1;
        const limit = this.getRoomLimit(row);
        const member = this.buildMemberArray(row);
        if (member.length < limit) {
          const tip = limit >= 2 ? this.$T('请选择人员1和人员2') : this.$T('请选择入住人员');
          this.$message.warning(tip + ' (' + n + ')');
          return;
        }
        if (limit >= 2 && member[0] === member[1]) {
          this.$message.warning(this.$T('同一房间不能选择相同人员') + ' (' + n + ')');
          return;
        }
      }
      this.saveLoading = true;
      const payload = this.assignmentList.map((row) => ({
        room_type_id: Number(row.room_type_id),
        checkin_date: row.checkin_date,
        checkout_date: row.checkout_date,
        room_index: Number(row.room_index),
        member: this.buildMemberArray(row),
      }));
      this.$api.post('/api/isai/roomMemberSave', {
        baoming_id: this.formData.id,
        assignment_list: JSON.stringify(payload),
      }, () => {
        this.saveLoading = false;
        this.$message.success(this.$T('保存成功'));
        rbLink({
          name: 'ISaiApplyDetail',
          query: {
            id: this.formData.id,
          },
        }, 'back');
      }, (res) => {
        this.saveLoading = false;
        this.$message.error(res.msg || this.$T('保存失败'));
      });
    },
  },
};
</script>



// WEBPACK FOOTER //
// SaiApplyFormStep3RoomMember.vue?788a9bc5